import { Fragment } from 'react';
import { useLocation } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiGet, apiPost } from '@/utils/api';
import { useAuthStore } from '@/stores/authStore';
import { useTechLocked } from '@/components/Tech/lockState';
// 🔴 effective_from is DATE-ONLY. new Date('2026-09-17') is UTC midnight,
// which renders as the previous day anywhere west of Greenwich — so a notice
// would appear to take effect a day early. formatDate parses it as a local
// calendar day.
import { formatDate } from '@/utils/dueDates';

/**
 * The notice, shown at first login and again whenever it is revised.
 *
 * 🔴 KEYED ON "HAS NOT ACKNOWLEDGED THE CURRENT VERSION", NOT ON "IS NEW".
 * Tying it to account creation would miss every account that already exists —
 * on a live deployment, all of them — and would never ask again when the
 * notice changes. Version N+1 makes everybody pending, which is what a revised
 * notice is FOR.
 *
 * 🔴 IT SHOWS NOTHING WHEN NOTHING IS PUBLISHED. `pending` is false both when
 * the person has acknowledged and when the organisation has published no
 * notice at all. An organisation with no notice asks its people for nothing —
 * which is the honest state, and the reason the first version is written by a
 * person rather than seeded.
 *
 * NOT DISMISSIBLE BY CLICKING AWAY, and not blocking either: there is no
 * Cancel, because declining is not a state this records, but the app behind it
 * still works. DPDPA wants a clear affirmative action, and a dialog somebody
 * escapes past has not obtained one.
 */
export default function NoticePrompt() {
  const queryClient = useQueryClient();
  // FM Technician B17: the technician's first run (/tech/welcome) asks for the
  // same acknowledgement as one of its steps; this prompt would cover it.
  const onFirstRun = useLocation().pathname === '/tech/welcome';
  // FM Technician final review: NOT OVER THE PIN LOCK. A revised notice that
  // arrives while the technician's lock is up would open as the newest dialog
  // and make the lock inert — under its own opaque screen, a keypad nobody
  // could press. The notice waits for the unlock (nothing is lost: it is
  // keyed on "not yet acknowledged", so it is still pending then).
  const locked = useTechLocked(useAuthStore((s) => s.user?.id ?? null));

  const { data } = useQuery<{
    pending: boolean;
    notice: { version: number; body: string; effective_from: string } | null;
    what_you_are_agreeing_to: string | null;
    withdrawal_effect: string;
  }>({
    queryKey: ['privacy', 'pending'],
    queryFn: () => apiGet('/privacy/notice/pending'),
    staleTime: 5 * 60 * 1000,
  });

  const accept = useMutation({
    mutationFn: () => apiPost('/privacy/notice/accept', {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['privacy'] }),
  });

  if (locked || onFirstRun || !data?.pending || !data.notice) return null;

  return (
    <Transition appear show as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={() => { /* no escape */ }}>
        <div className="fixed inset-0 bg-scrim/50" aria-hidden="true" />
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Dialog.Panel
              data-testid="notice-prompt"
              className="w-full max-w-2xl rounded-2xl bg-surface p-6 shadow-xl"
            >
              <Dialog.Title className="text-lg font-semibold text-content">
                Privacy notice
              </Dialog.Title>
              <p className="mt-1 text-sm text-content-tertiary">
                Version {data.notice.version}, in effect from{' '}
                {formatDate(data.notice.effective_from, 'dateLong')}.
              </p>

              <pre
                data-testid="notice-prompt-body"
                className="mt-4 max-h-80 overflow-auto whitespace-pre-wrap rounded-xl border border-line bg-surface-subtle p-4 text-sm text-content"
              >
                {data.notice.body}
              </pre>

              {/* What is being asked, named at the point of asking — not left
                  to be inferred from the notice text. */}
              {data.what_you_are_agreeing_to && (
                <p className="mt-4 text-sm text-content">
                  {data.what_you_are_agreeing_to}
                </p>
              )}

              {/* 🔴 AND WHAT ACKNOWLEDGING DOES NOT DO. Said here, before the
                  button, so nobody presses it believing they have consented to
                  processing they could later stop. */}
              <p className="mt-2 text-xs text-content-subtle">
                {data.withdrawal_effect}
              </p>

              <div className="mt-6 flex items-center justify-between gap-4">
                {/* Where to ask for a copy of what is held, or for it to be
                    erased — the right the notice describes, reachable from it.
                    A new tab, so the prompt is still here afterwards. */}
                <a
                  href="/privacy/request"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-brand-700 hover:underline"
                  data-testid="notice-request-data"
                >
                  Request your data
                </a>
                <button
                  className="btn-primary"
                  data-testid="accept-notice"
                  disabled={accept.isPending}
                  onClick={() => accept.mutate()}
                >
                  {accept.isPending ? 'Recording…' : 'I have read this notice'}
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
